import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import QuestionItem from "./QuestionItem";
import FeedbackBox from "./FeedbackBox";
import { theme } from "../../../../../theme";
import { addOrUpdateHiringExperienceQuestions, deleteHiringExperienceQuestion, getHiringExperienceQuestions } from "../preboardingConfigurationApi";
import { useParams } from "react-router-dom";
import CustomQuestions from "./CustomQuestions";
import { useSnackbar } from "../../../../../Context/SnackbarContext";

export interface Question {
  QuestionNum: number;
  Question: string;
  Answer: string;
}

const HiringExperienceConfiguration: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [customQuestions, setCustomQuestions] = useState<Question[]>([])
  const [feedback, setFeedback] = useState("");
  const { tenant } = useParams<{ tenant: string }>();
  const { showSnackbar } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionNumToDelete, setQuestionNumToDelete] = useState<number | null>(null);
  const [loading, setLoading] = useState(true)

  const cancelDeleteQuestion = () => {
    setQuestionNumToDelete(null);
    setDeleteDialogOpen(false);
  }

  const handleAnswerChange = (id: number, answer: string) => {
    setQuestions(prev =>
      prev.map(q => (q.QuestionNum === id ? { ...q, answer } : q))
    );
  };

  const fetchQuestions = async () => {
    const payLoad = {
      MapID:null
    }
    try {
      const result = await getHiringExperienceQuestions(tenant,payLoad,false)
      if(result){
        setFeedback(result.Feedback || "")
        setQuestions(result.Questions)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }

  }

  useEffect(() => {
    fetchQuestions()
  }, [tenant])

  const confirmDelete = async () => {
    if (questionNumToDelete === null) return;
    if( questions.length === 1) {
      showSnackbar("At least one question is required", "error");
      return;
    }
    try {
      const result = await deleteHiringExperienceQuestion(questionNumToDelete, tenant);
      if (result) {
        showSnackbar(result.Message, result.Status ? "success" : "error");
        if (result.Status) {
          setQuestions(prev => prev.filter(q => q.QuestionNum !== questionNumToDelete));
        }
      }
    } catch (error) {
      console.log(error)
      showSnackbar("Failed to delete question", "error");
    } finally {
      setQuestionNumToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleUpdateCustomQuestion = async (question_num: number, question: string) => {
    try {
      // Add your save logic here
      const payLoad = {
        Question: question,
        QuestionNum: question_num,
      }

      const result = await addOrUpdateHiringExperienceQuestions(payLoad, tenant);
      if (result) {
        showSnackbar(result.Message, result.Status ? "success" : "error");
        if (result.Status) {
          fetchQuestions();
        }
      }

    } catch (error) {
      console.log(error)
      showSnackbar("Failed to save question", "error");
    }
  }


  const handleAddCustomQuestion = () => {
    const newQuestion: Question = {
      QuestionNum: 0,
      Question: "",
      Answer: "",
    };
    setCustomQuestions(prev => [...prev, newQuestion]);
  };

  const handleDeleteCustomQuestion = (index: number) => {
    setCustomQuestions((prev) => prev.filter((_, i) => i !== index));
  }

  const handleChangeCustomQuestion = (index: number, value: string) => {
    setCustomQuestions(prev => {
      const updated = [...prev];
      updated[index].Question = value;
      return updated;
    })
  }

  if(loading){
    return(
      <Box sx={{display:'flex',alignItems:'center',justifyContent:'center',width:'100%',mt:'2vw'}}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box width={'100%'}>
      {/* Main Section */}
      <Box sx={{ display: 'flex', gap: '0.3vw', flexDirection: 'column', mt: '1vw' }}>
        <Typography variant="h6" fontWeight={600} >
          Hiring Experience
        </Typography>
        <Typography variant="body2" mb={1}>
          Please share your feedback on our hiring process. Let us know what worked well and where we can improve.
        </Typography>

        <Divider sx={{ mb: '0.5vw', width: '100%' }} />
      </Box>


      <Box sx={{ display: 'flex', alignItems: 'start' }}>
        <Box flex={2}>
          {questions.map((q, index) => (
            <QuestionItem
              key={q.QuestionNum}
              index={index + 1}
              question={q}
              onAnswerChange={handleAnswerChange}
              handleUpdateCustomQuestion={handleUpdateCustomQuestion}
              setQuestionNumToDelete={setQuestionNumToDelete}
              setDeleteDialogOpen={setDeleteDialogOpen}
              displayingForAdmin={true}
            />
          ))}
        </Box>
        <FeedbackBox feedback={feedback} setFeedback={setFeedback} displayingForAdmin={true} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddCustomQuestion}
          sx={{
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            ml: "auto"
          }}
        >
          Add Custom Question
        </Button>
      </Box>

      {
        customQuestions.length > 0 && (
          <CustomQuestions
            customQuestions={customQuestions}
            handleDeleteCustomQuestion={handleDeleteCustomQuestion}
            handleChangeCustomQuestion={handleChangeCustomQuestion}
            fetchQuestions={fetchQuestions}
          />
        )
      }

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteQuestion}
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this question? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteQuestion} sx={{ color: theme.palette.text.secondary }}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={{
              bgcolor: theme.palette.error.main,
              '&:hover': {
                bgcolor: theme.palette.error.dark,
              },
            }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default HiringExperienceConfiguration;
